import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  FileText,
  Download,
  Eye,
  RefreshCw,
  Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientStatementsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [statements, setStatements] = useState([]);
  const [yearFilter, setYearFilter] = useState('all');

  const fetchStatements = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/api/client/statements`, { headers });
      setStatements(response.data || []);
    } catch (error) {
      console.error('Error fetching statements:', error);
      setStatements([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStatements();
  }, [fetchStatements]);

  // Normalize status from backend to UI-friendly value
  const getStatus = (stmt) => {
    const s = stmt.status || '';
    if (s === 'generated' || s === 'generated_no_upload' || s === 'available') return 'available';
    return 'pending';
  };

  // Get the period display text
  const getPeriod = (stmt) => {
    if (stmt.period_label) return stmt.period_label;
    if (stmt.period) return stmt.period;
    if (stmt.period_start && stmt.period_end) {
      const start = new Date(stmt.period_start);
      return start.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
    }
    return '-';
  };

  const getStatementType = (stmt) => {
    const t = stmt.statement_type || stmt.type || 'monthly';
    const labels = { monthly: 'Monthly', quarterly: 'Quarterly', annual: 'Annual', custom: 'Custom' };
    return labels[t] || t;
  };

  const handleDownload = async (stmt) => {
    const status = getStatus(stmt);
    if (status !== 'available') return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/api/client/statements/${stmt.id}/download`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', stmt.file_name || `statement-${stmt.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download statement. Please contact support.');
    }
  };

  const handleView = async (stmt) => {
    const status = getStatus(stmt);
    if (status !== 'available') return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/api/client/statements/${stmt.id}/view`, { headers });
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('View error:', error);
      toast.error('Failed to view statement. Please try downloading instead.');
    }
  };

  // Get unique years for filter
  const years = [...new Set(statements.map(s => s.year).filter(Boolean))].sort((a, b) => b - a);

  // Filter statements
  const filteredStatements = statements.filter(stmt =>
    yearFilter === 'all' || stmt.year === parseInt(yearFilter)
  );

  if (loading) {
    return (
      <div className="space-y-6" data-testid="statements-loading">
        <Skeleton className="h-8 w-64" />
        <Card><CardContent className="p-6"><Skeleton className="h-64 w-full" /></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-statements-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#212529] font-['Manrope']">
            Statements
          </h1>
          <p className="text-[#6C757D] mt-1">
            View and download your monthly account statements
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="w-32" data-testid="year-filter">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {years.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={fetchStatements}
            className="flex items-center gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Statements Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#1D2D2A]" />
            Account Statements
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="text-xs font-semibold">Period</TableHead>
                  <TableHead className="text-xs font-semibold">Type</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStatements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                      No statements available yet
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStatements.map((stmt) => {
                    const status = getStatus(stmt);
                    return (
                      <TableRow key={stmt.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">
                          {getPeriod(stmt)}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {getStatementType(stmt)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={
                            status === 'available'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }>
                            {status === 'available' ? 'Available' : 'Pending'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleView(stmt)}
                              disabled={status !== 'available'}
                              title="View Statement"
                              data-testid={`view-btn-${stmt.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownload(stmt)}
                              disabled={status !== 'available'}
                              title="Download Statement"
                              data-testid={`download-btn-${stmt.id}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Statements are typically generated by the 5th of each month for the previous month.
            If you require a statement urgently or have questions, please contact support.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
